import React, { useState, useEffect, forwardRef, useImperativeHandle, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faQrcode, faCheck, faArrowLeft, faCalendar, faClock, faLocationDot, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Alert, Button, Modal, ModalHeader, ModalBody, ModalFooter, Toast, ToastHeader, ToastBody } from 'reactstrap';
import moment from 'moment';
import useApiFetch from '../../ApiFetch';

const MyPass = forwardRef((props, ref) => {
    const { event, retrievePass, registrationCode, passCode, eventCode, iconOnly, buttonSize, className, viewPass } = props;
    const { eventCode: paramEventCode  } = useParams();
    const apiFetch = useApiFetch();

    const [retrieving, setRetrieving] = useState(true);
    const [eventPasses, setEventPasses] = useState([]);

    const [sendingPass, setSendingPass] = useState(false);
    const [passSent, setPassSent] = useState(false);

    const [modal, setModal] = useState(false);

    useImperativeHandle(ref, () => ({
        handleMyPass() {
            handleMyPass();
        }
    }));


    const modalToggle = () => {
        setModal(!modal)
        if (modal) {
            setEventPasses([]);
            setRetrieving(true);
            setSendingPass(false);
            setPassSent(false);
        }
    };

    useEffect(() => {
        setRetrieving(true);

        if ((retrievePass && passCode) || viewPass) {
            handleMyPassButton();
        }
    }, []);
    const getData = async () => {
        setRetrieving(true);
        if (registrationCode) {
            await apiFetch.get('/api/event/' + encodeURIComponent(eventCode ?? paramEventCode) + '/Registration/' + encodeURIComponent(registrationCode) + '/Pass')
                .then(resp => {
                    setEventPasses(resp.data);
                    setRetrieving(false);
                });
        }
        else if (passCode) {
            await apiFetch.get('/api/event/' + encodeURIComponent(eventCode ?? paramEventCode) + '/Pass/' + encodeURIComponent(passCode))
                .then(resp => {
                    setEventPasses([{ ...resp.data }]);
                    setRetrieving(false);
                });
        }
        else {
            await apiFetch.get('/api/event/' + encodeURIComponent(eventCode ?? paramEventCode) + '/Pass')
                .then(resp => {
                    setEventPasses(resp.data);
                    setRetrieving(false);
                });
        }
    };
    const getEventDates = () => {
        if (event) {
            if (event.endDate) {
                //if we have an end date, check to see if the start and end dates are the same, if they are, show just one date
                if (event.endDate === event.startDate) {
                    return moment(event.startDate).format('MMMM D');
                }
                else {
                    //if the month is the same, show Month Start-End, otherwise, show Month Start - Month End
                    if (moment(event.startDate).month() === moment(event.endDate).month()) {
                        return moment(event.startDate).format('MMMM') + ' ' + moment(event.startDate).format('D') + '-' + moment(event.endDate).format('D');
                    }
                    else {
                        return moment(event.startDate).format('MMMM D') + '-' + moment(event.endDate).format('MMMM D');
                    }
                }
            }
            else {
                return moment(event.startDate).format('MMMM D');
            }
        }

    };
    const getEventTimes = () => {
        if (event && event.startTime) {
            if (event.endTime) {
                //if we have an end date, check to see if the start and end dates are the same, if they are, show just one date
                if (event.endTime === event.startTime) {
                    return moment(event.startTime, 'HH:mm:ss').format('h:mm A');
                }
                else {
                    return moment(event.startTime, 'HH:mm:ss').format('h:mm A') + '-' + moment(event.endTime, 'HH:mm:ss').format('h:mm A');
                }
            }
            else {
                return moment(event.startTime, 'HH:mm:ss').format('h:mm A');
            }
        }
        else {
            return null;
        }

    };

    const handleMyPassButton = async () => {
        getData();
        modalToggle();
    };
    const handleEmailPassButton = async (e) => {
        e.stopPropagation();
        setSendingPass(true);
        if (eventPasses?.length === 1) {
            await apiFetch.post('/api/event/emailpass/', eventPasses[0].passId, {
                headers: { 'Content-Type': 'application/json' }
            })
                .then(resp => {
                    if (resp.status === 200) {
                        setPassSent(true);
                        setSendingPass(false);
                    }
                });
        }
        else if (eventPasses?.length > 1 && registrationCode) {
            await apiFetch.post('/api/event/emailpasses/', registrationCode, {
                headers: { 'Content-Type': 'application/json' }
            })
                .then(resp => {
                    if (resp.status === 200) {
                        setPassSent(true);
                        setSendingPass(false);
                    }
                });
        }
    };
    const handleMyPass = () => {
        handleMyPassButton();
    };


    return (
        <>
            <button type="button" className={`${className} btn btn-primary ${buttonSize === 'lg' && 'btn-lg'} me-2`} onClick={handleMyPassButton}>
                {!iconOnly && <>My Pass{eventPasses?.length > 1 && 'es'}</>}
                <FontAwesomeIcon icon={faQrcode} className={`${!iconOnly && 'ps-2'}`} />
            </button>
            <Modal isOpen={modal} toggle={modalToggle} size="md" fullscreen={false} fade={true} className="modal-fullscreen-sm-down">
                <ModalBody className="m-0 p-0" onClick={modalToggle}>
                    <div className="row flex-lg-row align-items-center g-0 py-0 shadow-lg">
                        <div className="col-12 col-sm-12 col-lg-12 text-center">
                            <img style={{ maxHeight: '200px' }} src={event && event.imageUrl ? event.imageUrl : './assets/images/hyetech_logo2_4color.png'} className="img-fluid rounded-start rounded-end" alt="..." />
                        </div>
                        <div className="col-12 col-sm-12 col-lg-12 px-2 pt-3 text-center">
                            <div className="card-body">
                                <h1 className="h4 text-body-emphasis lh-1 mb-2">{event.title}</h1>
                                <p className="pt-0 small">
                                    <FontAwesomeIcon icon={faCalendar} /> {getEventDates()} &nbsp;
                                    {getEventTimes() ? <><FontAwesomeIcon icon={faClock} /> {getEventTimes()}</> : null}
                                    {event.venue || event.room
                                        ?
                                        <>
                                            <br />
                                            <FontAwesomeIcon icon={faLocationDot} />&nbsp;
                                            {event.venue ?? event.room}
                                        </>
                                        :
                                        null}
                                </p>
                                {!(eventPasses?.length > 0 || !retrieving) ?
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faSpinner} spin className="display-1 h1" />
                                        <h2 className="pt-3">Retrieving Pass...</h2>
                                    </div>
                                    : 
                                    <>
                                        {eventPasses?.length === 1 ?
                                            <div className="text-center py-3 px-5 mx-2 mx-sm-5">
                                                <img className="d-block mx-auto img-fluid" src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=10&data=${eventPasses[0].passId}`} alt="" />
                                                <h2 className="pt-2 mb-0">{eventPasses[0].firstName} {eventPasses[0].lastName}</h2>
                                                {eventPasses[0].company ? <h3 className="lead">{eventPasses[0].company}</h3> : null}
                                            </div>
                                            :
                                            <div id="eventPassCarousel" className="carousel slide">
                                                <div className="carousel-inner">
                                                    {eventPasses && eventPasses.length > 0 && eventPasses.map((ep, i) =>
                                                        <div className={`carousel-item ${i === 0 ? 'active' : ''}`} key={i}>
                                                            <div className="text-center py-3 px-5 mx-2 mx-sm-5">
                                                                <small><small>{i + 1} of {eventPasses.length}</small></small>
                                                                <img className="d-block mx-auto img-fluid" src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=10&data=${ep.passId}${i !== 0 ? '&color=333645' : ''}`} alt="" />
                                                                <h2 className="pt-2 mb-0">{ep.firstName} {ep.lastName}</h2>
                                                                {ep.company ? <h3 className="lead">{ep.company}</h3> : null}
                                                            </div>
                                                        </div>
                                                    )
                                                    }
                                                </div>
                                                <button onClick={(e) => e.stopPropagation()} className="carousel-control-prev" type="button" data-bs-target="#eventPassCarousel" data-bs-slide="prev">
                                                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Previous</span>
                                                </button>
                                                <button onClick={(e) => e.stopPropagation()} className="carousel-control-next" type="button" data-bs-target="#eventPassCarousel" data-bs-slide="next">
                                                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Next</span>
                                                </button>
                                            </div>
                                            }
                                        <div className="text-center py-3 px-5 mx-2 mx-sm-5">
                                            {passSent ?
                                                <>Email Sent</>
                                                :
                                                <button type="button" className="btn btn-primary px-4 me-md-2" onClick={handleEmailPassButton} disabled={sendingPass}>
                                                    Email Pass{eventPasses?.length > 1 && 'es'} To Me
                                                    <FontAwesomeIcon icon={sendingPass ? faSpinner : faPaperPlane} spin={sendingPass} className="ms-2" />
                                                </button>
                                            }
                                        </div>
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
});

export default MyPass;